import { useEffect, useRef } from 'react'
import QRCode from 'qrcode'
import { buildQrUrl } from '../config'

interface QrMakerProps {
  handle: string
  size?: number
  darkColor?: string
  lightColor?: string
  frame?: 'none' | 'rounded' | 'label'
}

export function drawYowoOverlay(ctx: CanvasRenderingContext2D, canvasSize: number) {
  const fontSize = Math.round(canvasSize * 0.062)
  const cx = canvasSize / 2
  const cy = canvasSize / 2

  // YO/WO: bold non-italic; u/n't.: italic regular weight (matches Logo.tsx tagline)
  const boldFont = `900 normal ${fontSize}px Inter, Arial, sans-serif`
  const italicFont = `italic 400 ${fontSize}px Inter, Arial, sans-serif`

  // Measure each segment with its own font so x-positions are exact
  ctx.font = boldFont
  const wYO = ctx.measureText('YO').width
  const wWO = ctx.measureText('WO').width
  ctx.font = italicFont
  const wU = ctx.measureText('u ').width
  const wNt = ctx.measureText("n't.").width

  const totalWidth = wYO + wU + wWO + wNt
  const padX = fontSize * 0.55
  const padY = fontSize * 0.38
  const rectW = totalWidth + padX * 2
  const rectH = fontSize + padY * 2
  const rx = cx - rectW / 2
  const ry = cy - rectH / 2
  const radius = rectH * 0.28

  // White pill background
  ctx.fillStyle = '#FFFFFF'
  ctx.strokeStyle = '#E2E8F0'
  ctx.lineWidth = Math.max(1, canvasSize * 0.004)
  ctx.beginPath()
  ctx.roundRect(rx, ry, rectW, rectH, radius)
  ctx.fill()
  ctx.stroke()

  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'

  // Horizontal gradient spanning YO…WO (matches brand-text-gradient)
  const gradX0 = rx + padX
  const gradX1 = gradX0 + wYO + wU + wWO
  const grad = ctx.createLinearGradient(gradX0, cy, gradX1, cy)
  grad.addColorStop(0, '#2563eb')
  grad.addColorStop(0.6, '#0ea5b7')
  grad.addColorStop(1, '#06b6d4')

  let x = rx + padX

  // "YO" — bold + gradient
  ctx.font = boldFont
  ctx.fillStyle = grad
  ctx.fillText('YO', x, cy)
  x += wYO

  // "u " — italic + slate
  ctx.font = italicFont
  ctx.fillStyle = '#94A3B8'
  ctx.fillText('u ', x, cy)
  x += wU

  // "WO" — bold + gradient
  ctx.font = boldFont
  ctx.fillStyle = grad
  ctx.fillText('WO', x, cy)
  x += wWO

  // "n't." — italic + slate
  ctx.font = italicFont
  ctx.fillStyle = '#94A3B8'
  ctx.fillText("n't.", x, cy)
}

const QrMaker = ({ handle, size = 200, darkColor = '#000000', lightColor = '#FFFFFF', frame = 'rounded' }: QrMakerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!canvasRef.current || !handle) return

    const generateQR = async () => {
      try {
        const url = buildQrUrl(handle)
        const dpiMultiplier = 2
        const canvasSize = size * dpiMultiplier

        if (canvasRef.current) {
          canvasRef.current.width = canvasSize
          canvasRef.current.height = canvasSize
          canvasRef.current.style.width = `${size}px`
          canvasRef.current.style.height = `${size}px`
        }

        await QRCode.toCanvas(canvasRef.current, url, {
          width: canvasSize,
          margin: 2,
          errorCorrectionLevel: 'H',
          color: {
            dark: darkColor,
            light: lightColor
          }
        })

        const ctx = canvasRef.current?.getContext('2d')
        if (ctx && frame === 'label') drawYowoOverlay(ctx, canvasSize)
      } catch (error) {
        console.error('Error generating QR code:', error)
      }
    }

    generateQR()
  }, [handle, size, darkColor, lightColor, frame])

  return (
    <div className={`inline-block p-2 ${frame === 'none' ? '' : 'rounded-xl'}`} style={{ backgroundColor: lightColor }}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`QR code for ${handle}`}
        className="block border border-slate-200 rounded-lg"
        style={{
          maxWidth: `${size}px`,
          maxHeight: `${size}px`,
          width: '100%',
          height: '100%'
        }}
      />
    </div>
  )
}

export default QrMaker
